import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DateFormatPipe, StatusBadgePipe } from '../../../../shared/pipes';
import { FieldTest, CustomFieldTest } from '../../../../core/models';
import { FieldTestService } from '../../../../core/services';
import { DroneService } from '../../../../core/services/drone.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-field-tests',
  standalone: true,
  imports: [CommonModule, DateFormatPipe, StatusBadgePipe],
  templateUrl: './field-tests.component.html',
  styleUrls: ['./field-tests.component.scss']
})
export class FieldTestsComponent implements OnInit {
  // Inject services
  private fieldTestService = inject(FieldTestService);
  private droneService = inject(DroneService);

  // Signal-based state from service
  readonly fieldTests = this.fieldTestService.fieldTests;
  readonly customTests = this.fieldTestService.customTests;
  readonly isLoading = this.fieldTestService.isLoading;

  // Computed stats from service
  readonly passedCount = this.fieldTestService.passedCount;
  readonly failedCount = this.fieldTestService.failedCount;
  readonly pendingCount = this.fieldTestService.pendingCount;

  // View toggle (local state)
  activeTab = signal<'records' | 'templates'>('records');

  constructor(private router: Router) {}

  ngOnInit(): void {
    // Load drones for name resolution
    this.droneService.loadOrganizationDrones();
  }

  uploadCustomTest(): void {
    this.router.navigate(['/logbook/testing/field/custom/add']);
  }

  viewCustomTest(test: CustomFieldTest): void {
    this.router.navigate(['/logbook/testing/field/custom', test.id]);
  }

  editCustomTest(test: CustomFieldTest): void {
    this.router.navigate(['/logbook/testing/field/custom', test.id, 'edit']);
  }

  deleteCustomTest(test: CustomFieldTest): void {
    // TODO: Implement delete confirmation modal
    this.fieldTestService.deleteCustomTest(test.id);
  }

  viewCustomTestFile(file: { name: string; filePath: string }): void {
    const fullUrl = this.getFullUrl(file.filePath);
    window.open(fullUrl, '_blank');
  }

  /**
   * Get full CDN URL from file path
   */
  private getFullUrl(filePath: string): string {
    if (!filePath) return '';

    // If already a full URL (backwards compatibility), return as-is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }

    // Prepend CDN URL
    const cdnUrl = environment.appCDN.endsWith('/')
      ? environment.appCDN.slice(0, -1)
      : environment.appCDN;
    const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    return `${cdnUrl}/${cleanPath}`;
  }

  getCustomTestTypeIcon(type: string): string {
    return this.fieldTestService.getCustomTestTypeIcon(type as any);
  }

  getCustomTestTypeLabel(type: string): string {
    return this.fieldTestService.getCustomTestTypeLabel(type as any);
  }

  getCategoryLabel(category: string): string {
    return this.fieldTestService.getCategoryLabel(category);
  }

  getFrequencyLabel(frequency: string): string {
    return this.fieldTestService.getFrequencyLabel(frequency);
  }

  getFileIcon(type: string): string {
    return this.fieldTestService.getFileIcon(type);
  }

  addFieldTest(): void {
    this.router.navigate(['/logbook/testing/field/add']);
  }

  viewTest(test: FieldTest): void {
    this.router.navigate(['/logbook/testing/field', test.id]);
  }

  editTest(test: FieldTest): void {
    this.router.navigate(['/logbook/testing/field', test.id, 'edit']);
  }

  deleteTest(test: FieldTest): void {
    // TODO: Implement delete confirmation modal
    this.fieldTestService.deleteFieldTest(test.id);
  }

  getStatusText(status: string): string {
    return this.fieldTestService.getStatusText(status as any);
  }

  getTestTypeBadgeClass(type: string): string {
    return this.fieldTestService.getTestTypeBadgeClass(type as any);
  }

  getTestTypeLabel(type: string): string {
    return this.fieldTestService.getTestTypeLabel(type as any);
  }

  getPassedChecklistCount(test: FieldTest): number {
    return this.fieldTestService.getPassedChecklistCount(test);
  }

  /**
   * Get the display name for applicable aircraft field
   * Returns drone name if ID matches, otherwise returns raw value
   */
  getAircraftDisplayName(applicableAircraft: string): string {
    if (!applicableAircraft || applicableAircraft === 'all') {
      return 'All Aircraft';
    }

    // Try to find a matching drone by ID
    const drone = this.droneService.getDroneById(applicableAircraft);
    if (drone) {
      return drone.name;
    }

    // Return raw value if not found (might be a legacy string like "DJI Agras T40")
    return applicableAircraft;
  }
}
